import { RESOURCE_CENTER_PATH } from '@sly/core/constants/dashboardAppPaths';
import cx from 'classnames';
import { bool, node, string } from 'prop-types';
import React, { forwardRef, useRef, useState } from 'react';
import { Chevron, Close, Dropdown, Menu, Seniorly } from 'sly/icons/index';
import HeaderMenuList from 'sly/resourceCenter/components/Header/HeaderMenuList';
import { usePrefetch } from 'sly/services/api/prefetch';
import Link from 'sly/system/Link';

const backToSeniorlyItem = {
  label: 'Back to Seniorly.com',
  iconBack: true,
  to: '/',
  hideInBigScreen: true,
};

const Wrapper = forwardRef(
  ({ children, onBlur, menuIsOpen, ...props }, ref) => (
    <div
      ref={ref}
      className={cx(
        'relative bg-white p-m outline-none',
        menuIsOpen && 'fixed top-0 z-[3] w-full rounded-none',
        'md:px-l',
        'lg:h-xxxl lg:py-0',
        menuIsOpen &&
          'position-[initial] top-[initial] z-[initial] w-[initial] rounded-[initial]',
        props.className
      )}
      tabIndex='-1'
      onBlur={onBlur}
      data-tp-id='gen-div-f7ad5c53-f4d9-492f-8d21-2a3ddd87914d'
    >
      {children}
    </div>
  )
);

Wrapper.propTypes = {
  children: node,
  menuIsOpen: bool,
};

const getMenuItem = (item, setMenuIsOpen, className) => (
  <Link
    className={cx('hover:no-underline', className && className)}
    key={item.value}
    to={item.to}
    {...(setMenuIsOpen && { onClick: () => setMenuIsOpen(false) })}
    data-tp-id='gen-Link-5545c147-ee1e-4510-93a4-d29fb7f8d841'
  >
    {item.label}
    {item.iconBack && <Chevron className='rotate-90' />}
  </Link>
);

const getMenuItems = (menuItems, setMenuIsOpen) =>
  menuItems?.map((item, index) => {
    if (index === menuItems.length - 1) {
      return getMenuItem(item, setMenuIsOpen);
    }
    return getMenuItem(
      item,
      setMenuIsOpen,
      'lg:!text-viridian-base !text-slate-base '
    );
  });
const getRouteToRedirect = (slug) => `${RESOURCE_CENTER_PATH}/${slug}`;

const Header = ({
  headerMenuItems,
  logoText,
  logoClickPath,
  showTopicSection,
}) => {
  const [menuIsOpen, setMenuIsOpen] = useState(false);

  const headerMenuRef = useRef(null);

  const toggleDropdown = () => {
    setMenuIsOpen(!menuIsOpen);
  };

  const handleHeaderMenuBlur = (e) => {
    // trigger blur event handler only if focus is on an element outside dropdown, mind it
    if (
      menuIsOpen &&
      headerMenuRef.current &&
      !headerMenuRef.current.contains(e.relatedTarget)
    ) {
      toggleDropdown();
    }
  };

  return (
    <Wrapper
      style={{ tabIndex: '-1' }}
      className={cx(
        'flex flex-wrap justify-between lg:items-center',
        'border-b-[1px] border-solid border-slate-lighter-90'
      )}
      menuIsOpen={menuIsOpen}
      onBlur={handleHeaderMenuBlur}
    >
      <div
        className='lg:my-auto lg:mx-0'
        data-tp-id='gen-div-aca7175d-db41-45f7-9aa8-f8d9c2b51819'
      >
        <Link
          className='flex items-center hover:no-underline'
          to={logoClickPath}
          data-tp-id='gen-Link-261aaaf0-88b5-4d51-ba60-b0742fc43153'
        >
          <span data-tp-id='gen-span-b6e72245-f149-4ed1-bb01-7a15b3ad0dbc'>
            <Seniorly size='xl' />
          </span>
          <div
            className='font-b-m ml-xxs whitespace-nowrap md:text-xl'
            data-tp-id='gen-div-b4173ca2-e44f-443d-8b50-e0d762e23e1c'
          >
            <b>seniorly</b> {logoText}
          </div>
        </Link>
      </div>

      <div
        className='pt-xs md:pt-0 lg:hidden'
        data-tp-id='gen-div-bdb4816f-1115-4ebd-90e2-b986e439e8f6'
      >
        <div
          className='flex items-center md:h-m'
          data-tp-id='gen-div-2ffbf847-4503-48d8-b14c-a1b8777aaf93'
        >
          {menuIsOpen ? (
            <Close
              className='cursor-pointer'
              onClick={() => setMenuIsOpen(!menuIsOpen)}
              cursor='pointer'
              data-testid='MenuIcon'
            />
          ) : (
            <Menu
              className='cursor-pointer'
              onClick={() => setMenuIsOpen(!menuIsOpen)}
              data-testid='MenuIcon'
            />
          )}
        </div>
      </div>

      <div
        className='resourceCenterHeader hidden lg:flex'
        data-tp-id='gen-div-10f65c61-df47-46c6-8f69-99f817692c63'
      >
        {headerMenuItems?.length > 0 && showTopicSection && (
          <div
            className='mr-m'
            onClick={toggleDropdown}
            data-tp-id='gen-div-dd3d1b2d-c483-4118-8beb-a91cbdcc91a4'
          >
            Topics
            <Dropdown
              color='text-viridian-base'
              className={cx(menuIsOpen && 'rotate-180')}
            />
          </div>
        )}

        {getMenuItem(backToSeniorlyItem)}
      </div>

      {menuIsOpen && (
        <HeaderMenuList
          listItems={getMenuItems(headerMenuItems, setMenuIsOpen)}
          ref={headerMenuRef}
          menuIsOpen={menuIsOpen}
        />
      )}
    </Wrapper>
  );
};

const HeaderContainer = ({
  fetchTopicHeader = true,
  logoClickPath = RESOURCE_CENTER_PATH,
  logoText = 'resource center',
  showTopicSection = true,
}) => {
  const getHeaderMenuItems = (result) => [
    ...(result?.map(({ slug, name }) => ({
      label: name,
      value: name,
      to: getRouteToRedirect(slug),
    })) || []),
    backToSeniorlyItem,
  ];
  let headerMenuItems = [backToSeniorlyItem];
  const {
    requestInfo: { result },
  } = usePrefetch('getTopicHeader');

  if (fetchTopicHeader) {
    headerMenuItems = getHeaderMenuItems(result);
  }

  return (
    <Header
      fetchTopicHeader={fetchTopicHeader}
      logoText={logoText}
      headerMenuItems={headerMenuItems}
      logoClickPath={logoClickPath}
      showTopicSection={showTopicSection}
    />
  );
};

HeaderContainer.propTypes = {
  fetchTopicHeader: bool,
  logoText: string,
  logoClickPath: string,
  showTopicSection: bool,
};

export default HeaderContainer;
